import '../styles/pages/admin.css';

import React, { useEffect, useRef, useState } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { animated, useSpring } from 'react-spring';
import { motion, AnimatePresence } from 'framer-motion';

// Material UI Icons
import BusinessIcon from '@mui/icons-material/Business';
import CategoryIcon from '@mui/icons-material/Category';
import HomeIcon from '@mui/icons-material/Home';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import MenuIcon from '@mui/icons-material/Menu';
import PersonIcon from '@mui/icons-material/Person';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import TuneIcon from '@mui/icons-material/Tune';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';

// Importy komponentów
import Categories from './admin/categories/Categories';
import Inquiries from './admin/inquiries/Inquiries';
import MainPage from './admin/main/MainPage';
import Offers from './admin/offers/Offers';
import Orders from './admin/orders/Orders';
import Parameters from './admin/parameters/Parameters';
import Producers from './admin/producer/Producers';
import Products from './admin/products/Products';
import Users from './admin/users/Users';
import Returns from './admin/returns/Returns';
import ProductGroups from './admin/productGroups/ProductGroups';
import Seo from '../components/seo/Seo';

function Admin() {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const [isSidePanelOpen, setIsSidePanelOpen] = useState(!isMobile);
  const sidePanelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const path = location.pathname.split('/')[2] || '';
    setActiveSection(path);

    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile && !isSidePanelOpen) {
        setIsSidePanelOpen(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [location.pathname, isSidePanelOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isMobile &&
        sidePanelRef.current &&
        !sidePanelRef.current.contains(event.target as Node)
      ) {
        setIsSidePanelOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobile]);

  const slideStyle = useSpring({
    transform: isSidePanelOpen ? 'translateX(0%)' : 'translateX(-100%)',
    config: { tension: 300, friction: 40 },
  });

  // Funkcja do renderowania elementu nawigacji - podobnie jak w Profile.tsx
  const renderNavItem = (
    section: string,
    url: string,
    label: string,
    icon: React.ReactNode,
  ) => {
    const isActive = activeSection === url;
    const fullUrl = `/admin/${url}`;

    return (
      <motion.button
        onClick={() => {
          navigate(fullUrl);
          if (isMobile) setIsSidePanelOpen(false);
        }}
        className={`
          flex items-center gap-3 px-4 py-3 rounded-lg w-full transition-all
          ${isActive ? 'bg-purple-100 text-purple-700 font-medium' : 'hover:bg-gray-50 text-gray-700'}
        `}
        whileHover={{
          scale: 1.02,
          backgroundColor: isActive
            ? 'rgba(147, 51, 234, 0.1)'
            : 'rgba(0, 0, 0, 0.03)',
        }}
        whileTap={{ scale: 0.98 }}
      >
        <div className={isActive ? 'text-purple-700' : 'text-gray-500'}>
          {icon}
        </div>
        <span className="text-sm">{label}</span>
      </motion.button>
    );
  };

  return (
    <>
      <div className="mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {/* Nagłówek panelu admin */}
        <div className="py-6 md:py-8 lg:py-10">
          <div className="mb-4 sm:mb-8">
            <motion.h1
              className="text-2xl sm:text-3xl font-bold text-gray-900"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Panel administracyjny
            </motion.h1>
            <motion.p
              className="text-base text-gray-600 mt-1"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              Zarządzaj produktami, zamówieniami i ustawieniami sklepu
            </motion.p>
          </div>

          {/* Karta z informacjami o panelu */}
          <motion.div
            className="bg-gradient-to-r from-purple-600 to-indigo-600 border border-purple-300 rounded-xl shadow-md p-4 sm:p-6 md:flex md:items-center md:justify-between mb-8 text-white"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <div className="flex items-center gap-4">
              <div className="bg-white/20 h-14 w-14 rounded-full flex items-center justify-center">
                <AdminPanelSettingsIcon style={{ fontSize: '2rem' }} />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Panel Administratora</h2>
                <p className="text-sm text-purple-100">
                  Pełny dostęp do zarządzania sklepem
                </p>
              </div>
            </div>

            {isMobile && (
              <motion.button
                className="flex items-center gap-2 mt-4 md:mt-0 bg-white/20 hover:bg-white/30 transition-colors py-2 px-4 rounded-lg"
                onClick={() => setIsSidePanelOpen(true)}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                <MenuIcon />
                <span>Menu</span>
              </motion.button>
            )}
          </motion.div>
        </div>

        {/* Główna zawartość */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Pasek nawigacyjny - desktop */}
          {!isMobile && (
            <motion.div
              className="col-span-1"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: 0.4 }}
            >
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-3 sticky top-24">
                <h3 className="text-xs uppercase text-purple-700 font-semibold px-4 py-2 border-b border-gray-100 mb-2">
                  Panel Główny
                </h3>
                <div className="space-y-1">
                  {renderNavItem(
                    'Panel Główny',
                    '',
                    'Panel Główny',
                    <HomeIcon fontSize="small" />,
                  )}
                </div>

                <h3 className="text-xs uppercase text-purple-700 font-semibold px-4 py-2 border-b border-gray-100 mt-6 mb-2">
                  Sprawy Handlowe
                </h3>
                <div className="space-y-1">
                  {renderNavItem(
                    'Sprawy Handlowe',
                    'zamówienia',
                    'Zamówienia',
                    <ShoppingBagIcon fontSize="small" />,
                  )}
                  {renderNavItem(
                    'Sprawy Handlowe',
                    'zapytania',
                    'Zapytania',
                    <QuestionAnswerIcon fontSize="small" />,
                  )}
                  {renderNavItem(
                    'Sprawy Handlowe',
                    'oferty',
                    'Oferty',
                    <LocalOfferIcon fontSize="small" />,
                  )}
                  {renderNavItem(
                    'Sprawy Handlowe',
                    'zwroty',
                    'Zwroty',
                    <InventoryIcon fontSize="small" />,
                  )}
                </div>

                <h3 className="text-xs uppercase text-purple-700 font-semibold px-4 py-2 border-b border-gray-100 mt-6 mb-2">
                  Zarządzanie Treścią
                </h3>
                <div className="space-y-1">
                  {renderNavItem(
                    'Zarządzanie Treścią',
                    'produkty',
                    'Produkty',
                    <InventoryIcon fontSize="small" />,
                  )}
                  {renderNavItem(
                    'Zarządzanie Treścią',
                    'grupy-produktów',
                    'Grupy produktów',
                    <InventoryIcon fontSize="small" />,
                  )}
                  {renderNavItem(
                    'Zarządzanie Treścią',
                    'producenci',
                    'Producenci',
                    <BusinessIcon fontSize="small" />,
                  )}
                  {renderNavItem(
                    'Zarządzanie Treścią',
                    'kategorie',
                    'Kategorie',
                    <CategoryIcon fontSize="small" />,
                  )}
                  {renderNavItem(
                    'Zarządzanie Treścią',
                    'parametry',
                    'Parametry',
                    <TuneIcon fontSize="small" />,
                  )}
                </div>

                <h3 className="text-xs uppercase text-purple-700 font-semibold px-4 py-2 border-b border-gray-100 mt-6 mb-2">
                  Użytkownicy
                </h3>
                <div className="space-y-1">
                  {renderNavItem(
                    'Użytkownicy',
                    'użytkownicy',
                    'Użytkownicy',
                    <PersonIcon fontSize="small" />,
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* Menu mobilne */}
          {isMobile && (
            <AnimatePresence>
              {isSidePanelOpen && (
                <motion.div
                  className="fixed inset-0 bg-black/50 z-40"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setIsSidePanelOpen(false)}
                />
              )}
            </AnimatePresence>
          )}

          {isMobile && (
            <animated.div
              className="fixed left-0 top-0 h-full bg-white shadow-xl z-50 w-[280px] overflow-y-auto"
              ref={sidePanelRef}
              style={slideStyle}
            >
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h3 className="font-semibold text-purple-700">Panel Admin</h3>
                <button
                  onClick={() => setIsSidePanelOpen(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  &times;
                </button>
              </div>

              <div className="p-3">
                <h3 className="text-xs uppercase text-purple-700 font-semibold px-4 py-2 border-b border-gray-100 mb-2">
                  Panel Główny
                </h3>
                <div className="space-y-1">
                  {renderNavItem(
                    'Panel Główny',
                    '',
                    'Panel Główny',
                    <HomeIcon fontSize="small" />,
                  )}
                </div>

                <h3 className="text-xs uppercase text-purple-700 font-semibold px-4 py-2 border-b border-gray-100 mt-6 mb-2">
                  Sprawy Handlowe
                </h3>
                <div className="space-y-1">
                  {renderNavItem(
                    'Sprawy Handlowe',
                    'zamówienia',
                    'Zamówienia',
                    <ShoppingBagIcon fontSize="small" />,
                  )}
                  {renderNavItem(
                    'Sprawy Handlowe',
                    'zapytania',
                    'Zapytania',
                    <QuestionAnswerIcon fontSize="small" />,
                  )}
                  {renderNavItem(
                    'Sprawy Handlowe',
                    'oferty',
                    'Oferty',
                    <LocalOfferIcon fontSize="small" />,
                  )}
                  {renderNavItem(
                    'Sprawy Handlowe',
                    'zwroty',
                    'Zwroty',
                    <InventoryIcon fontSize="small" />,
                  )}
                </div>

                <h3 className="text-xs uppercase text-purple-700 font-semibold px-4 py-2 border-b border-gray-100 mt-6 mb-2">
                  Zarządzanie Treścią
                </h3>
                <div className="space-y-1">
                  {renderNavItem(
                    'Zarządzanie Treścią',
                    'produkty',
                    'Produkty',
                    <InventoryIcon fontSize="small" />,
                  )}
                  {renderNavItem(
                    'Zarządzanie Treścią',
                    'grupy-produktów',
                    'Grupy produktów',
                    <InventoryIcon fontSize="small" />,
                  )}
                  {renderNavItem(
                    'Zarządzanie Treścią',
                    'producenci',
                    'Producenci',
                    <BusinessIcon fontSize="small" />,
                  )}
                  {renderNavItem(
                    'Zarządzanie Treścią',
                    'kategorie',
                    'Kategorie',
                    <CategoryIcon fontSize="small" />,
                  )}
                  {renderNavItem(
                    'Zarządzanie Treścią',
                    'parametry',
                    'Parametry',
                    <TuneIcon fontSize="small" />,
                  )}
                </div>

                <h3 className="text-xs uppercase text-purple-700 font-semibold px-4 py-2 border-b border-gray-100 mt-6 mb-2">
                  Użytkownicy
                </h3>
                <div className="space-y-1">
                  {renderNavItem(
                    'Użytkownicy',
                    'użytkownicy',
                    'Użytkownicy',
                    <PersonIcon fontSize="small" />,
                  )}
                </div>
              </div>
            </animated.div>
          )}

          {/* Treść główna */}
          <motion.div
            className="col-span-1 md:col-span-3 bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={location.pathname}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
                className="p-4 sm:p-6"
              >
                <Routes>
                  <Route path="/" element={<MainPage />} />
                  <Route path="/oferty/*" element={<Offers />} />
                  <Route path="/zapytania/*" element={<Inquiries />} />
                  <Route path="/produkty/*" element={<Products />} />
                  <Route path="/parametry/*" element={<Parameters />} />
                  <Route path="/kategorie/*" element={<Categories />} />
                  <Route path="/producenci/*" element={<Producers />} />
                  <Route path="/zamówienia/*" element={<Orders />} />
                  <Route path="/użytkownicy/*" element={<Users />} />
                  <Route path="/zwroty/*" element={<Returns />} />
                  <Route
                    path="/grupy-produktów/*"
                    element={<ProductGroups />}
                  />
                </Routes>
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
      <Seo title="Panel Administracyjny"></Seo>
    </>
  );
}

export default Admin;
